import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ServiceModuleModel } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { TimeTrackingOverviewTable } from './components/TimeTrackingOverviewTable'
import { TimeTrackingExportDialog } from './components/TimeTrackingExportDialog'
import { ExportTimeTrackingButton } from './header-actions-components/ExportTimeTrackingButton'
import { TimeTrackingAllEntriesPage } from './all-entries-components/TimeTrackingAllEntriesPage'
import { TimeTrackingEmployeeEntriesPage } from './all-entries-components/TimeTrackingEmployeeEntriesPage'
import { useModuleSubView } from '../useModuleSubView'

interface Props {
  module: ServiceModuleModel
}

/**
 * Time-tracking service module as its own page (card click → `?open`). Built like the
 * absence module: the overview (employee metrics + search + row dialogs) sits
 * entirely in `TimeTrackingOverviewTable`; the page only holds the header. The
 * sub-view is URL-driven (`useModuleSubView`, `?view=all` /
 * `?view=employee&employeeId=…`), so every view has a shareable URL and Back/Forward
 * works: header button "all time entries" → `TimeTrackingAllEntriesPage`,
 * row click → `TimeTrackingEmployeeEntriesPage`.
 */
export function TimeTrackingServiceModulePage({ module }: Props) {
  const { t } = useTranslation('service-modules-timetracking')
  const { view, showOverview, showAll, showEmployee } = useModuleSubView()
  const [exportAllOpen, setExportAllOpen] = useState(false)

  if (view.mode === 'all') {
    return <TimeTrackingAllEntriesPage module={module} onBack={showOverview} />
  }
  if (view.mode === 'employee') {
    return <TimeTrackingEmployeeEntriesPage module={module} employeeId={view.employeeId} onBack={showOverview} />
  }

  return (
    <PageLayout>
      <PageHeader title={module.name || t('timetracking.title')} description={t('timetracking.description')}>
        <PageHeaderActions>
          <ExportTimeTrackingButton onClick={() => setExportAllOpen(true)} />
          <Button onClick={showAll}>
            {t('timetracking.showAll')}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <TimeTrackingOverviewTable
        module={module}
        onRowClick={(employee) => employee.id && showEmployee(employee.id)}
      />

      <TimeTrackingExportDialog
        open={exportAllOpen}
        onOpenChange={setExportAllOpen}
        serviceModuleId={module.id}
      />
    </PageLayout>
  )
}
